import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  InputAdornment,
  FormControlLabel,
  Switch,
  FormGroup,
  FormLabel,
  ButtonGroup,
  Chip,
  Divider,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Schedule as TimeIcon,
  SwapVert as SwapIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Accessible as AccessibleIcon,
  ChildCare as ChildCareIcon,
  Luggage as LuggageIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../../contexts/LoadingContext';
import apiService from '../../services/apiService';
import LocationAutocomplete, { LocationData } from '../../components/Common/LocationAutocomplete';
import SocialShareModal from '../../components/Common/SocialShareModal';

const TIME_FLEXIBILITY_OPTIONS = [
  { value: 0, label: 'Exact' },
  { value: 1, label: '±1h' },
  { value: 2, label: '±2h' },
  { value: 4, label: '±4h' },
];

// Custom day component for DateTimePicker with bold and 50% larger font
const CustomPickersDay = (props: any) => {
  return (
    <PickersDay
      {...props}
      sx={{
        fontWeight: 'bold !important',
        fontSize: '1.5em !important', // 50% increase from default 1em
        ...props.sx
      }}
    />
  );
};

// interface City removed, using LocationData from LocationAutocomplete

const RequestRidePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setLoading } = useLoading();

  const [cities, setCities] = useState<any[]>([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [citiesMap, setCitiesMap] = useState<Map<string, any>>(new Map());

  const [formData, setFormData] = useState({
    // Route
    originCity: null as LocationData | null,
    destinationCity: null as LocationData | null,
    originDetails: '',
    destinationDetails: '',

    // Schedule
    preferredDateTime: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow
    timeFlexibility: 2, // ±2 hours

    // Trip Details
    passengerCount: 1,
    minBudget: '',
    maxBudget: '',

    // Special Requirements
    needsLargeLuggage: false,
    needsChildSeat: false,
    needsWheelchairAccess: false,
    specialRequirements: '',
    description: '',
    allowChat: true, // Allow passengers to send messages about this ride request
  });

  const [shareItem, setShareItem] = useState<{ type: 'ride' | 'request'; data: any } | null>(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cities are now loaded dynamically via Google Places API in the LocationAutocomplete component.
  // We can remove the entire useEffect that was preloading Canadian cities.
  useEffect(() => {
    setLoadingCities(false);
  }, []);

  const handleSwapCities = () => {
    setFormData(prev => ({
      ...prev,
      originCity: prev.destinationCity,
      destinationCity: prev.originCity,
      originDetails: prev.destinationDetails,
      destinationDetails: prev.originDetails,
    }));
  };

  const handlePassengerChange = (increment: boolean) => {
    setFormData(prev => ({
      ...prev,
      passengerCount: increment
        ? Math.min(8, prev.passengerCount + 1)
        : Math.max(1, prev.passengerCount - 1)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.originCity || !formData.destinationCity) {
      setError('Please select both origin and destination cities');
      return;
    }

    if (formData.originCity.id === formData.destinationCity.id) {
      setError('Origin and destination must be different cities');
      return;
    }

    if (formData.preferredDateTime <= new Date()) {
      setError('Preferred departure time must be in the future');
      return;
    }

    if (formData.passengerCount < 1 || formData.passengerCount > 8) {
      setError('Passenger count must be between 1 and 8');
      return;
    }

    if (formData.minBudget && formData.maxBudget) {
      const minBudget = parseFloat(formData.minBudget);
      const maxBudget = parseFloat(formData.maxBudget);
      if (minBudget > maxBudget) {
        setError('Minimum budget cannot be higher than maximum budget');
        return;
      }
    }

    setIsSubmitting(true);
    setLoading(true, 'Posting your request...');

    try {
      const requestData = {
        originCityId: formData.originCity!.id,
        originLat: formData.originCity!.latitude,
        originLng: formData.originCity!.longitude,
        originName: formData.originCity!.name,
        originProvince: formData.originCity!.province,

        destinationCityId: formData.destinationCity!.id,
        destLat: formData.destinationCity!.latitude,
        destLng: formData.destinationCity!.longitude,
        destName: formData.destinationCity!.name,
        destProvince: formData.destinationCity!.province,

        originDetails: formData.originDetails || undefined,
        destinationDetails: formData.destinationDetails || undefined,
        preferredDateTime: formData.preferredDateTime.toISOString(),
        timeFlexibility: formData.timeFlexibility,
        passengerCount: formData.passengerCount,
        minBudget: formData.minBudget ? parseFloat(formData.minBudget) : undefined,
        maxBudget: formData.maxBudget ? parseFloat(formData.maxBudget) : undefined,
        needsLargeLuggage: formData.needsLargeLuggage,
        needsChildSeat: formData.needsChildSeat,
        needsWheelchairAccess: formData.needsWheelchairAccess,
        specialRequirements: formData.specialRequirements || undefined,
        description: formData.description || undefined,
        allowChat: formData.allowChat,
      };

      console.log('Sending request data:', requestData);
      const response = await apiService.post('/requests', requestData);
      console.log('Create request response:', response);

      if (response.success) {
        console.log('Request created successfully, preparing share item');
        setShareItem({
          type: 'request',
          data: {
            id: response.data?.id || response.data?.request?.id,
            originCity: formData.originCity,
            destinationCity: formData.destinationCity,
            preferredDateTime: formData.preferredDateTime,
            passengerCount: formData.passengerCount
          }
        });
      } else {
        console.error('Request creation failed:', response);
        setError(response.message || 'Failed to create ride request. Please try again.');
      }
    } catch (error: any) {
      console.error('Create request error:', error);
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create ride request. Please try again.';
      setError(errorMessage);
    } finally {
      console.log('Request submission completed');
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  // Modern Gradient & Glass Styles
  const pageBackground = 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)';
  const cardGlass = {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  };

  const sectionHeaderStyle = {
    fontSize: '2rem',
    fontWeight: 800,
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    mb: 4,
  };

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '16px',
      bgcolor: 'rgba(255,255,255,0.5)',
      transition: 'all 0.3s ease',
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.8)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
      },
      '&.Mui-focused': {
        bgcolor: '#ffffff',
        boxShadow: '0 4px 20px rgba(99, 102, 241, 0.15)',
      }
    },
    '& .MuiInputLabel-root': { fontSize: '1.2rem', fontWeight: 600 },
    '& .MuiInputBase-input': { fontSize: '1.2rem', p: '16px 14px' } // Reset padding to standard
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{
        minHeight: '100vh',
        width: '100%',
        background: pageBackground,
        py: { xs: 4, md: 8 },
        px: { xs: 2, md: 4 }
      }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label={t('requestRide.passengerChip', 'Passenger')}
              sx={{
                bgcolor: '#e0e7ff',
                color: '#6366f1',
                fontWeight: 800,
                mb: 2,
                px: 2,
                height: 32
              }}
            />
            <Typography variant="h2" sx={{
              fontWeight: 900,
              letterSpacing: '-0.03em',
              mb: 2,
              background: 'linear-gradient(45deg, #1e293b, #6366f1)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {t('requestRide.title', 'Where do you need to go?')}
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 500, maxWidth: 600, mx: 'auto' }}>
              {t('requestRide.subtitle', 'Create a request and let top-rated drivers bid to get you there comfortably.')}
            </Typography>
          </Box>

          <Paper elevation={0} sx={{ ...cardGlass, p: { xs: 3, md: 6 } }}>
            {error && (
              <Alert severity="error" sx={{ mb: 4, borderRadius: '16px', fontSize: '1.1rem' }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={5}>
                {/* Route Section */}
                <Grid size={{ xs: 12 }}>
                  <Box sx={sectionHeaderStyle}>
                    <Box sx={{ p: 1.5, bgcolor: '#e0e7ff', borderRadius: '14px', color: '#6366f1', display: 'flex' }}>
                      <LocationIcon fontSize="large" />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="800">{t('requestRide.routeDetails', 'Route Details')}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('requestRide.routeSub', 'Start and end points')}</Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3} alignItems="flex-start">
                    <Grid size={{ xs: 12, md: 5 }}>
                      <LocationAutocomplete
                        label={t('requestRide.pickupCity', 'Pickup City')}
                        placeholder={t('requestRide.pickupPlaceholder', 'e.g. Tehran, Iran')}
                        icon={<LocationIcon sx={{ color: '#ef4444', fontSize: '1.5rem' }} />}
                        value={formData.originCity}
                        onChange={(newValue: any) => setFormData({ ...formData, originCity: newValue })}
                        modeColor="#6366f1"
                      />
                      <TextField
                        fullWidth
                        label={t('requestRide.pickupDetails', 'Specific Pickup Address')}
                        value={formData.originDetails}
                        onChange={(e) => setFormData({ ...formData, originDetails: e.target.value })}
                        margin="normal"
                        placeholder={t('requestRide.pickupPlaceholder', 'e.g. 123 Main St, Terminal 1')}
                        sx={inputStyle}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 2 }} sx={{ display: 'flex', justifyContent: 'center', pt: { xs: 0, md: 3 } }}>
                      <Box
                        onClick={handleSwapCities}
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          bgcolor: '#f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { bgcolor: '#e0e7ff', color: '#6366f1', transform: 'rotate(180deg)' }
                        }}
                      >
                        <SwapIcon fontSize="large" />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 5 }}>
                      <LocationAutocomplete
                        label={t('requestRide.dropoffCity', 'Destination City')}
                        placeholder={t('requestRide.dropoffPlaceholder', 'e.g. Istanbul, Turkey')}
                        icon={<LocationIcon sx={{ color: '#ef4444', fontSize: '1.5rem' }} />}
                        value={formData.destinationCity}
                        onChange={(newValue: any) => setFormData({ ...formData, destinationCity: newValue })}
                        modeColor="#6366f1"
                      />
                      <TextField
                        fullWidth
                        label={t('requestRide.dropoffDetails', 'Specific Drop-off Address')}
                        value={formData.destinationDetails}
                        onChange={(e) => setFormData({ ...formData, destinationDetails: e.target.value })}
                        margin="normal"
                        placeholder={t('requestRide.dropoffPlaceholder', 'e.g. Hotel Downtown')}
                        sx={inputStyle}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12 }}><Divider sx={{ opacity: 0.6 }} /></Grid>

                {/* Date & Time */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={sectionHeaderStyle}>
                    <Box sx={{ p: 1.5, bgcolor: '#ecfdf5', borderRadius: '14px', color: '#10b981', display: 'flex' }}>
                      <TimeIcon fontSize="large" />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="800">{t('requestRide.timing', 'Timing')}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('requestRide.timingSub', 'When do you want to leave?')}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <DateTimePicker
                      label={t('requestRide.preferredDeparture', 'Preferred Departure')}
                      value={formData.preferredDateTime}
                      onChange={(newValue) => newValue && setFormData({ ...formData, preferredDateTime: newValue })}
                      minDateTime={new Date()}
                      slots={{ day: CustomPickersDay }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          sx: inputStyle
                        }
                      }}
                    />

                    <FormControl fullWidth>
                      <FormLabel sx={{ mb: 1, fontWeight: 600, color: '#64748b' }}>{t('requestRide.flexibility', 'Flexibility')}</FormLabel>
                      <Box sx={{ display: 'flex', gap: 1, p: 1, bgcolor: '#f8fafc', borderRadius: '16px' }}>
                        {TIME_FLEXIBILITY_OPTIONS.map((option) => (
                          <Button
                            key={option.value}
                            variant={formData.timeFlexibility === option.value ? 'contained' : 'text'}
                            onClick={() => setFormData({ ...formData, timeFlexibility: option.value })}
                            sx={{
                              flex: 1,
                              borderRadius: '12px',
                              py: 1.5,
                              textTransform: 'none',
                              fontWeight: 700,
                              bgcolor: formData.timeFlexibility === option.value ? '#10b981' : 'transparent',
                              color: formData.timeFlexibility === option.value ? 'white' : '#64748b',
                              '&:hover': {
                                bgcolor: formData.timeFlexibility === option.value ? '#059669' : 'rgba(0,0,0,0.05)',
                              }
                            }}
                          >
                            {/* Note: 'Exact' might need translation if options were dynamic, but keys are fixed here. */}
                            {option.label}
                          </Button>
                        ))}
                      </Box>
                    </FormControl>
                  </Box>
                </Grid>

                {/* Budget & Pax */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={sectionHeaderStyle}>
                    <Box sx={{ p: 1.5, bgcolor: '#fff7ed', borderRadius: '14px', color: '#f97316', display: 'flex' }}>
                      <MoneyIcon fontSize="large" />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="800">{t('requestRide.details', 'Details')}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('requestRide.detailsSub', 'Budget and passengers')}</Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        label={t('requestRide.minBudget', 'Min Budget')}
                        value={formData.minBudget}
                        onChange={(e) => setFormData({ ...formData, minBudget: e.target.value })}
                        type="number"
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        sx={inputStyle}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        label={t('requestRide.maxBudget', 'Max Budget')}
                        value={formData.maxBudget}
                        onChange={(e) => setFormData({ ...formData, maxBudget: e.target.value })}
                        type="number"
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        sx={inputStyle}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 600, color: '#64748b' }}>{t('requestRide.passengers', 'Passengers')}</FormLabel>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        bgcolor: 'white'
                      }}>
                        <Button
                          onClick={() => handlePassengerChange(false)}
                          disabled={formData.passengerCount <= 1}
                          sx={{ minWidth: 48, height: 48, borderRadius: '12px', bgcolor: '#f1f5f9', fontSize: '1.5rem' }}
                        >
                          −
                        </Button>
                        <Typography variant="h5" fontWeight="800" color="primary">{formData.passengerCount}</Typography>
                        <Button
                          onClick={() => handlePassengerChange(true)}
                          disabled={formData.passengerCount >= 8}
                          sx={{ minWidth: 48, height: 48, borderRadius: '12px', bgcolor: '#f1f5f9', fontSize: '1.5rem' }}
                        >
                          +
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12 }}><Divider sx={{ opacity: 0.6 }} /></Grid>

                {/* Requirements */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={sectionHeaderStyle}>
                    <Box sx={{ p: 1.5, bgcolor: '#f3e8ff', borderRadius: '14px', color: '#a855f7', display: 'flex' }}>
                      <SettingsIcon fontSize="large" />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="800">{t('requestRide.preferences', 'Preferences')}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('requestRide.preferencesSub', 'Special needs and extras')}</Typography>
                    </Box>
                  </Box>

                  <FormGroup sx={{ gap: 2 }}>
                    {[
                      { label: t('requestRide.largeLuggage', 'Large Luggage'), icon: <LuggageIcon />, checked: formData.needsLargeLuggage, key: 'needsLargeLuggage' },
                      { label: t('requestRide.childSeat', 'Child Seat'), icon: <ChildCareIcon />, checked: formData.needsChildSeat, key: 'needsChildSeat' },
                      { label: t('requestRide.wheelchair', 'Wheelchair Access'), icon: <AccessibleIcon />, checked: formData.needsWheelchairAccess, key: 'needsWheelchairAccess' },
                    ].map((item) => (
                      <Paper key={item.key} elevation={0} sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: item.checked ? '#a855f7' : '#e2e8f0',
                        bgcolor: item.checked ? '#f3e8ff' : 'transparent',
                        borderRadius: '16px',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer'
                      }}
                        onClick={() => setFormData({ ...formData, [item.key]: !item.checked })}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ color: item.checked ? '#a855f7' : '#94a3b8' }}>{item.icon}</Box>
                          <Typography fontWeight="600" color={item.checked ? '#a855f7' : '#475569'}>{item.label}</Typography>
                        </Box>
                        <Switch checked={item.checked} sx={{ pointerEvents: 'none' }} />
                      </Paper>
                    ))}
                  </FormGroup>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label={t('requestRide.additionalNotes', 'Additional Notes')}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    multiline
                    rows={6}
                    placeholder={t('requestRide.notesPlaceholder', 'Tell drivers about your trip...')}
                    sx={{ ...inputStyle, mt: 10 }}
                  />

                  <Box sx={{ mt: 3, p: 2, borderRadius: '16px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                    <FormControlLabel
                      control={<Switch checked={formData.allowChat} onChange={(e) => setFormData({ ...formData, allowChat: e.target.checked })} />}
                      label={<Typography fontWeight="600" color="#1e40af">{t('requestRide.allowChat', 'Allow drivers to message me')}</Typography>}
                    />
                  </Box>
                </Grid>

                {/* Action Bar */}
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
                    <Button
                      size="large"
                      variant="outlined"
                      onClick={() => navigate('/find-rides')}
                      sx={{
                        borderRadius: '16px',
                        px: 4, py: 1.5,
                        fontWeight: 700,
                        borderWidth: 2,
                        '&:hover': { borderWidth: 2 }
                      }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                      type="submit"
                      size="large"
                      variant="contained"
                      disabled={isSubmitting}
                      sx={{
                        borderRadius: '16px',
                        px: 6, py: 1.5,
                        fontWeight: 800,
                        fontSize: '1.1rem',
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        boxShadow: '0 8px 16px rgba(99, 102, 241, 0.25)'
                      }}
                    >
                      {isSubmitting ? t('common.posting', 'Posting...') : t('requestRide.postRequest', 'Request a Ride')}
                    </Button>
                  </Box>
                </Grid>

              </Grid>
            </form>
          </Paper>
        </Container>
      </Box>

      {shareItem && (
        <SocialShareModal
          open={!!shareItem}
          onClose={() => {
            setShareItem(null);
            navigate('/my-rides', {
              state: {
                message: `Request posted! Drivers will be able to see and bid on your trip from ${formData.originCity?.name} to ${formData.destinationCity?.name}.`,
                type: 'success'
              }
            });
          }}
          type={shareItem.type}
          data={shareItem.data}
          showSuccess={true}
        />
      )}
    </LocalizationProvider>
  );
};

export default RequestRidePage;